import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DOMPurify from "dompurify";
import { Marked } from "marked";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import parseOutputSegments from "../lib/parseOutputSegments";
import { ModalStates } from "../states/ModalStates";

const markedInstance = new Marked({
  headerIds: false,
  mangle: false,
  gfm: true,
  renderer: {
    paragraph(text) {
      return `<div>${text}</div><div>&nbsp;</div>`;
    },
  },
});

const RESIZE_MSG_TYPE = "ai-preview-resize";

const HtmlPreview = ({ html }) => {
  const iframeRef = useRef(null);

  const srcDoc = useMemo(
    () =>
      `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
  body { margin: 0; padding: 8px; font-family: Arial, sans-serif; font-size: 14px; }
</style>
</head>
<body>${html}<script>
var resizePending = false;
var lastHeight = 0;
new ResizeObserver(function(){
  if (resizePending) return;
  resizePending = true;
  requestAnimationFrame(function(){
    resizePending = false;
    var height = document.body.scrollHeight;
    if (height === lastHeight) return;
    lastHeight = height;
    parent.postMessage({type:"${RESIZE_MSG_TYPE}",h:height},"*");
  });
}).observe(document.body);
</script></body>
</html>`,
    [html],
  );

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) {
      return;
    }

    const handleMessage = (e) => {
      if (
        e.data?.type === RESIZE_MSG_TYPE &&
        e.source === iframe.contentWindow
      ) {
        iframe.style.height = `${e.data.h + 16}px`;
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  return (
    <iframe
      ref={iframeRef}
      srcDoc={srcDoc}
      sandbox="allow-scripts"
      title="HTML Preview"
      style={{
        width: "100%",
        minHeight: "100px",
        border: "1px solid #dee2e6",
        borderRadius: "4px",
        background: "#fff",
      }}
    />
  );
};

const sanitize = (html) => DOMPurify.sanitize(html);

const MarkdownBlock = ({ content }) => {
  const ref = useRef(null);

  const renderedHtml = useMemo(
    () => sanitize(markedInstance.parse(content || "")),
    [content],
  );

  useEffect(() => {
    if (ref.current) {
      ref.current.innerHTML = renderedHtml;
    }
  }, [renderedHtml]);

  return <div ref={ref} />;
};

const SCROLL_THRESHOLD = 30;

const StreamingView = ({ text, markdownContentRef }) => {
  const [userScrolledUp, setUserScrolledUp] = useState(false);
  const isAutoScrolling = useRef(false);

  const renderedHtml = useMemo(
    () => sanitize(markedInstance.parse(text || "")),
    [text],
  );

  const handleScroll = useCallback(() => {
    if (isAutoScrolling.current) {
      return;
    }

    const el = markdownContentRef.current;
    if (!el) {
      return;
    }

    const atBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD;
    setUserScrolledUp(!atBottom);
  }, [markdownContentRef]);

  useEffect(() => {
    if (markdownContentRef.current) {
      markdownContentRef.current.innerHTML = renderedHtml;

      if (!userScrolledUp) {
        isAutoScrolling.current = true;
        markdownContentRef.current.scrollTop =
          markdownContentRef.current.scrollHeight;
        requestAnimationFrame(() => {
          isAutoScrolling.current = false;
        });
      }
    }
  }, [renderedHtml, markdownContentRef, userScrolledUp]);

  return (
    <div
      ref={markdownContentRef}
      className="markdown overflow-auto"
      style={{ maxHeight: "400px" }}
      onScroll={handleScroll}
    />
  );
};

const FinishedView = ({ text, markdownContentRef }) => {
  const segments = useMemo(() => parseOutputSegments(text), [text]);

  return (
    <div
        ref={markdownContentRef}
        className="markdown overflow-auto"
        style={{ maxHeight: "400px" }}
      >
        {segments.map((segment, index) => {
          const key = `${segment.type}-${index}`;

          if (segment.type === "html-preview") {
            return <HtmlPreview key={key} html={segment.content} />;
          }

          return <MarkdownBlock key={key} content={segment.content} />;
        })}
      </div>
  );
};

const getLastUserMessage = (messages) => {
  if (!messages?.length) {
    return "";
  }
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === "user") {
      const parts = messages[i].parts;
      if (parts) {
        return parts
          .filter((p) => p.type === "text")
          .map((p) => p.text)
          .join("");
      }
      return messages[i].content || "";
    }
  }
  return "";
};

const GeneratingState = ({
  state,
  markdownContentRef,
}) => {
  const promptText = getLastUserMessage(state.messages);
  const isGenerating = state.status === ModalStates.GENERATING;

  return (
    <div className="card mb-2">
      <div className="card-header d-flex">
        <div className="flex-shrink-0 mr-1">
          {!isGenerating && (
            <FontAwesomeIcon
              icon="far fa-check-circle"
              className="text-green"
            />
          )}
        </div>

        <div
          className="overflow-auto"
          style={{ maxHeight: "100px", whiteSpace: "pre-wrap" }}
        >
          {promptText}
        </div>
      </div>

      <div className="card-body">
        {state.outputText && isGenerating && (
          <StreamingView
            text={state.outputText}
            markdownContentRef={markdownContentRef}
          />
        )}

        {state.outputText && !isGenerating && (
          <FinishedView
            text={state.outputText}
            markdownContentRef={markdownContentRef}
          />
        )}
      </div>
    </div>
  );
};

export default GeneratingState;
